<script>
import { computed, ref } from 'vue';
import AppBanner from '@/components/shared/AppBanner';
import Button from '../components/reusable/Button.vue';
import { useI18n } from 'vue-i18n';
import ProjectsGridHome from '../components/projects/ProjectsGridHome.vue';
import { useMediumArticles } from '../composables/useMediumArticles.js';
import { training } from '../data/teaching';
import { useLanguage } from '@/composables/useLanguage';

export default {
	name: 'Home',
	components: {
		AppBanner,
		ProjectsGridHome,
		Button,
	},
	setup() {
		const { t, tm, locale } = useI18n();
		const { currentLanguage } = useLanguage();
		const { articles, loading, error } = useMediumArticles('iqbalarauf');
		const profileSummary = computed(() => tm('about.bios').filter((bio) => bio.bio).slice(0, 2));
		const experience = computed(() => tm('about.experience'));
		const latestTraining = computed(() => training[locale.value].slice(-3).reverse());
		const resumeHref = computed(() => currentLanguage.value === 'id'
			? '/files/Resume-IqbalAR-ID.pdf'
			: '/files/Resume-IqbalAR-EN.pdf');
		const testingTools = ref([
			{ name: 'Cypress', src: require('@/assets/images/svg/cypress.svg'), tooltip: 'End-to-end testing framework' },
			{ name: 'Postman', src: require('@/assets/images/svg/postman.svg'), tooltip: 'API testing and development tool' },
			{ name: 'k6', src: require('@/assets/images/svg/k6.svg'), tooltip: 'Load testing tool' },
			{ name: 'GitLab', src: require('@/assets/images/svg/gitlab.svg'), tooltip: 'DevOps platform for CI/CD' },
			{ name: 'GitHub', src: require('@/assets/images/svg/github.svg'), tooltip: 'Version control and collaboration platform' },
			{ name: 'Katalon', src: require('@/assets/images/svg/katalon.svg'), tooltip: 'Automation testing tool' },
			{ name: 'SonarQube', src: require('@/assets/images/svg/sonarqube.svg'), tooltip: 'Continuous inspection of code quality' },
		]);
		const writingTools = ref([
			{ name: 'Google Docs', src: require('@/assets/images/svg/googledocs.svg'), tooltip: 'Collaborative document editing' },
			{ name: 'Wordpress', src: require('@/assets/images/svg/wordpress.svg'), tooltip: 'Content management system' },
			{ name: 'Canva', src: require('@/assets/images/svg/canva.svg'), tooltip: 'Graphic design and content creation' },
			{ name: 'Medium', src: require('@/assets/images/svg/medium.svg'), tooltip: 'Publishing platform for writers' },
		]);
		return {
			t,
			profileSummary,
			experience,
			latestTraining,
			resumeHref,
			testingTools,
			writingTools,
			articles,
			loading,
			error,
		};
	},
};
</script>

<template>
	<div class="container mx-auto">
		<!-- Banner -->
		<AppBanner class="mb-5 sm:mb-8" />

		<div class="grid grid-cols-1 sm:grid-cols-2 mt-10 sm:gap-10">
			<div
				class="md:mt-10 block border border-indigo-200 dark:border-ternary-dark py-2.5 sm:py-3 shadow-lg rounded-lg bg-secondary-light dark:bg-ternary-dark text-gray-500 dark:text-white">
				<div class="flex flex-row justify-center">
					<h2 class="font-general-semibold text-xl font-semibold mt-2 mb-3 px-4">Software Quality Assurance
					</h2>
				</div>
				<div class="flex flex-wrap justify-center mt-4">
					<img v-for="tool in testingTools" :key="tool.name" :src="tool.src" :title="tool.tooltip"
						class="w-16 h-16 m-2 hover:opacity-75 transition-opacity dark:invert" />
				</div>
			</div>
			<div
				class="mt-10 border align-center border-indigo-200 dark:border-ternary-dark py-2.5 sm:py-3 shadow-lg rounded-lg bg-secondary-light dark:bg-ternary-dark text-gray-500 dark:text-white">
				<div class="flex flex-row justify-center">
					<h2 class="font-general-semibold text-xl font-semibold mt-2 mb-3 px-4">Content Writing
					</h2>
				</div>
				<div class="flex flex-wrap justify-center mt-4">
					<img v-for="tool in writingTools" :key="tool.name" :src="tool.src" :title="tool.tooltip"
						class="w-16 h-16 m-2 hover:opacity-75 transition-opacity dark:invert" />
				</div>
			</div>
		</div>

		<!-- Projects -->
		<ProjectsGridHome />

		<!-- Latest training -->
		<section class="mt-10 sm:mt-20">
			<div class="text-center border-b border-primary-light dark:border-secondary-dark pb-8">
				<h2 class="font-general-semibold text-2xl sm:text-5xl font-semibold text-ternary-dark dark:text-ternary-light mb-4">
					{{ t('training.latestTrainingTitle') }}
				</h2>
				<p class="font-general-regular text-secondary-dark dark:text-ternary-light text-md sm:text-xl">
					{{ t('training.description') }}
				</p>
			</div>
			<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-8 sm:gap-10">
				<article v-for="trainingItem in latestTraining" :key="trainingItem.id"
					class="mb-8 sm:mb-0 overflow-hidden rounded-lg shadow-lg bg-secondary-light dark:bg-ternary-dark">
					<img :src="trainingItem.img" :alt="trainingItem.name" class="h-56 w-full object-cover" />
					<div class="px-5 py-6">
						<p class="font-general-regular text-sm text-primary-dark dark:text-gray-300 mb-2">{{ trainingItem.date }}</p>
						<h3 class="font-general-semibold text-xl text-ternary-dark dark:text-ternary-light mb-2">{{ trainingItem.name }}</h3>
						<p class="font-general-regular text-secondary-dark dark:text-gray-300">{{ trainingItem.organizer }}</p>
					</div>
				</article>
			</div>
			<div class="mt-2 sm:mt-5 flex justify-center">
				<router-link to="/teaching"
					class="font-general-medium flex items-center px-6 py-3 rounded-lg shadow-lg hover:shadow-xl bg-indigo-500 hover:bg-indigo-600 focus:ring-1 focus:ring-indigo-900 text-white text-lg duration-300">
					{{ t('home.loadTrainingMore') }}
				</router-link>
			</div>
		</section>

		<!-- Latest Medium Articles -->
		<div class="mt-10 sm:mt-20">
			<div class="text-center mb-6">
				<h2
					class="font-general-semibold text-2xl sm:text-5xl font-semibold mb-10 text-ternary-dark dark:text-ternary-light">
					{{ t('writing.latestTitle') }}
				</h2>
			</div>

			<!-- Loading State -->
			<div v-if="loading" class="flex justify-center items-center py-12">
				<div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
			</div>

			<!-- Error State -->
			<div v-else-if="error"
				class="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 p-4 rounded-lg text-center">
				{{ error }}
			</div>

			<!-- Articles Grid -->
			<div v-else-if="articles.length > 0" class="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10">
				<article v-for="article in articles.slice(0, 3)" :key="article.id"
					class="border border-gray-200 dark:border-ternary-dark rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 bg-secondary-light dark:bg-ternary-dark">
					<!-- Article Image -->
					<div class="h-48 bg-gray-300 dark:bg-gray-700 overflow-hidden">
						<img v-if="article.thumbnail" :src="article.thumbnail" :alt="article.title"
							class="w-full h-full object-cover hover:scale-105 transition-transform duration-300">
						<div v-else
							class="w-full h-full flex items-center justify-center bg-gradient-to-r from-blue-400 to-purple-500">
							<svg class="w-12 h-12 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
								viewBox="0 0 24 24" stroke="currentColor">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
									d="M12 6.253v13m0-13C6.5 6.253 2 10.998 2 17s4.5 10.747 10 10.747c5.5 0 10-4.998 10-10.747S17.5 6.253 12 6.253z" />
							</svg>
						</div>
					</div>

					<!-- Article Content -->
					<div class="p-5">
						<!-- Date -->
						<div
							class="flex items-center justify-between text-xs text-secondary-dark dark:text-gray-400 mb-4">
							<span>{{ article.pubDate }}</span>
						</div>

						<!-- Title -->
						<h4
							class="font-general-semibold text-lg font-semibold text-ternary-dark dark:text-ternary-light mb-2 line-clamp-2 text-left">
							{{ article.title }}
						</h4>

						<!-- Description -->
						<p class="text-secondary-dark dark:text-gray-300 text-sm mb-4 line-clamp-3 text-left">
							{{ article.description }}
						</p>

						<!-- Read More Button -->
						<a :href="article.link" target="_blank" rel="noopener noreferrer"
							class="inline-block w-full text-center bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
							Baca Selengkapnya
						</a>
					</div>
				</article>
			</div>

			<!-- No Articles -->
			<div v-else class="text-center py-12">
				<p class="text-secondary-dark dark:text-ternary-light">
					Tidak ada artikel yang ditemukan
				</p>
			</div>
		</div>

		<!-- Load more articles button -->
		<div class="mt-2 sm:mt-5 flex justify-center">
			<router-link to="/writing"
				class="font-general-medium flex items-center px-6 py-3 rounded-lg shadow-lg hover:shadow-xl bg-indigo-500 hover:bg-indigo-600 focus:ring-1 focus:ring-indigo-900 text-white text-lg sm:text-xl duration-300"
				aria-label="More Articles">
				<Button :title="t('home.loadArticleMore')" />
			</router-link>
		</div>

		<!-- CTA Section -->
		<div class="mt-10 sm:mt-20 text-center py-10 px-5 rounded-lg bg-secondary-light dark:bg-ternary-dark shadow-lg">
			<h2
				class="font-general-semibold text-2xl sm:text-4xl font-semibold mb-6 text-ternary-dark dark:text-ternary-light">
				{{ t('cta.title') }}
			</h2>
			<p class="text-lg mb-8 text-gray-600 dark:text-gray-300">
				{{ t('cta.description') }}
			</p>
			<div class="flex justify-center space-x-4">
				<a href="mailto:iqbalarauf.official@gmail.com" target="_blank" rel="noopener noreferrer"
					class="font-general-medium flex items-center px-6 py-3 rounded-lg shadow-lg hover:shadow-xl bg-blue-500 hover:bg-blue-600 focus:ring-1 focus:ring-blue-900 text-white text-lg duration-300">
					<Button :title="t('cta.emailbutton')" />
				</a>
				<a href="https://www.linkedin.com/in/iqbalarauf" target="_blank" rel="noopener noreferrer"
					class="font-general-medium flex items-center px-6 py-3 rounded-lg shadow-lg hover:shadow-xl bg-blue-700 hover:bg-blue-800 focus:ring-1 focus:ring-blue-900 text-white text-lg duration-300">
					<Button :title="t('cta.linkedinbutton')" />
				</a>
			</div>
		</div>
	</div>
</template>

<style scoped></style>